import { Toast, type ToastDismissReason } from '@figma/fpl-components';
import * as stylex from '@stylexjs/stylex';
import { Alert, Slide, Snackbar, type AlertColor } from '@mui/material';
import { ForwardedRef, forwardRef, isValidElement, useCallback, useEffect } from 'react';
import { color } from '@/colors';
import { FF_FPL_MIGRATION } from '@/consts/featureFlags';
import { ZIndex } from '@/consts/z-indexes.consts';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useWithLoader } from '@/hooks/useWithLoader';
import { useGlobalStore } from '@/state/global.state';
import { Box } from '@/UI/Box/Box';
import { CircularProgress } from '@/UI/CircularProgress/CircularProgress';
import { themeColor } from '@/UI/themeColors';
import { ButtonContained } from '../Buttons/AppButton';
import { ErrorAlert } from '../ErrorSnackbar/ErrorSnackbar';
import { FlexCenVer } from '../styles';
import type { AlertData, SnackbarData } from '@/state/global.state';

export type NotificationSeverity = NonNullable<AlertData['severity']>;

export function getFplToastVariant(severity: AlertData['severity']): 'default' | 'danger' {
  return severity === 'error' ? 'danger' : 'default';
}

export function getFplToastRole(severity: AlertData['severity']): 'alert' | 'status' {
  return severity === 'error' ? 'alert' : 'status';
}

export function getLegacyAlertSeverity(severity: AlertData['severity']): AlertColor {
  return severity === 'default' || severity === undefined ? 'success' : severity;
}

interface AppSnackbarContentProps {
  alertData: AlertData;
  onClose: () => void;
  isFpl: boolean;
  timeout?: number;
}

const AppSnackbarContent = forwardRef(function AppSnackbarContent(
  { alertData: { text, icon, action, severity, showCloseButton }, onClose, isFpl, timeout }: AppSnackbarContentProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const onAction = async () => await action?.onClick();
  const { isLoading, execute: handleAction } = useWithLoader(onAction, { sync: true });
  const hasProgressIcon = isValidElement(icon) && icon.type === CircularProgress;

  if (isFpl) {
    const isDismissible = showCloseButton === true && severity !== 'error';
    const handleFplClose = (reason: ToastDismissReason) => {
      if (reason === 'timeout' && timeout !== undefined) onClose();
      if (reason === 'dismiss' && isDismissible) onClose();
    };

    return (
      <Toast.Root
        data-testid="fpl-app-snackbar"
        variant={getFplToastVariant(severity)}
        timeout={timeout}
        onClose={handleFplClose}
      >
        {hasProgressIcon && <Toast.Spinner data-testid="fpl-app-snackbar-spinner" />}
        <Toast.Message role={getFplToastRole(severity)}>{text}</Toast.Message>
        {action && (
          <Toast.ActionButton
            action={() => {
              handleAction();
              return false;
            }}
            disabled={isLoading}
            loading={isLoading}
          >
            {action.text}
          </Toast.ActionButton>
        )}
        {isDismissible && <Toast.DismissButton />}
      </Toast.Root>
    );
  }

  const hideIcon = icon === null;
  const legacySeverity = getLegacyAlertSeverity(severity);

  if (legacySeverity === 'error') {
    return (
      <ErrorAlert
        ref={ref}
        errorMessage={text}
        action={
          action
            ? {
                label: action.text,
                onClick: handleAction,
                disabled: isLoading,
                loading: isLoading,
              }
            : undefined
        }
        sx={{ height: '40px' }}
      />
    );
  }

  return (
    <Alert
      ref={ref}
      severity={legacySeverity}
      variant="filled"
      icon={hideIcon ? false : icon}
      onClose={showCloseButton ? onClose : undefined}
      sx={{
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: themeColor.white100,
        py: 1.5,
        pl: 2,
        pr: action ? 1 : 2,
        borderRadius: 2,
        border: `1px solid ${color.White04_T}`,
        color: color.Black100,
        fontSize: '0.75rem',
        fontWeight: 400,
        whiteSpace: 'nowrap',
        '& .MuiAlert-message': { width: '100%' },
      }}
      slotProps={{ icon: { sx: { mr: !hideIcon ? 1 : 0 } } }}
    >
      <FlexCenVer sx={{ gap: 2, width: '100%', justifyContent: action ? 'space-between' : 'center' }}>
        <span>{text}</span>
        {action && (
          <ButtonContained
            mode={action.mode ?? 'filled-dark'}
            size="small"
            onClick={handleAction}
            endIcon={
              isLoading ? (
                <Box sx={{ mr: 0.5, display: 'inline-flex' }}>
                  <CircularProgress size={12} color="inherit" />
                </Box>
              ) : null
            }
            loading={isLoading}
            disabled={isLoading}
            sx={{
              flexShrink: 0,
              ...(action.mode === 'text' && {
                color: color.Black100,
                '&:hover': { bgcolor: color.Black08_T },
              }),
            }}
          >
            {action.text}
          </ButtonContained>
        )}
      </FlexCenVer>
    </Alert>
  );
});
AppSnackbarContent.displayName = 'AppSnackbarContent';

export interface AppSnackbarStandaloneProps {
  snackbarData: SnackbarData;
  onClose: () => void;
}

export const DEFAULT_AUTO_HIDE_DURATION = 5000;

const styles = stylex.create({
  fplHost: {
    position: 'fixed',
    top: 15,
    right: 8,
    left: 8,
    display: 'flex',
    justifyContent: 'center',
    pointerEvents: 'none',
  },
  fplEntry: {
    pointerEvents: 'auto',
  },
  zIndex: (value: number) => ({ zIndex: value }),
});

const SlideTransition = forwardRef(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (props: any, ref: ForwardedRef<HTMLDivElement>) => <Slide {...props} ref={ref} direction="down" />,
);
SlideTransition.displayName = 'SlideTransition';

export const AppSnackbarStandalone = ({ snackbarData, onClose }: AppSnackbarStandaloneProps) => {
  const isFpl = useFeatureFlag(FF_FPL_MIGRATION).value === true;
  const timeout = snackbarData.duration === null ? undefined : (snackbarData.duration ?? DEFAULT_AUTO_HIDE_DURATION);

  useEffect(() => {
    if (!isFpl || !snackbarData.isOpen || snackbarData.duration === null) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.key !== 'Escape') return;
      onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isFpl, onClose, snackbarData.duration, snackbarData.isOpen]);

  if (isFpl) {
    if (!snackbarData.isOpen) return null;
    return (
      <div
        {...stylex.props(styles.fplHost, styles.zIndex(ZIndex.Overlays.APP_SNACKBAR))}
        data-testid="fpl-app-snackbar-host"
      >
        <div {...stylex.props(styles.fplEntry)}>
          <AppSnackbarContent
            key={snackbarData.key}
            alertData={snackbarData}
            onClose={onClose}
            isFpl
            timeout={timeout}
          />
        </div>
      </div>
    );
  }

  const handleClose = snackbarData.duration === null ? undefined : onClose;
  return (
    <Snackbar
      key={snackbarData.key}
      open={snackbarData.isOpen}
      onClose={handleClose}
      autoHideDuration={snackbarData.duration === null ? null : timeout}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      TransitionComponent={SlideTransition}
      sx={{
        zIndex: ZIndex.Overlays.APP_SNACKBAR,
        top: '15px !important',
        ...(snackbarData.minWidth && { '& .MuiAlert-root': { minWidth: snackbarData.minWidth } }),
      }}
      ClickAwayListenerProps={{ onClickAway: () => {} }}
    >
      <AppSnackbarContent alertData={snackbarData} onClose={onClose} isFpl={false} />
    </Snackbar>
  );
};

export const AppSnackbar = () => {
  const { snackbarData, updateSnackbarData } = useGlobalStore();

  const handleClose = useCallback(
    () => updateSnackbarData({ text: '', isOpen: false, icon: null, showCloseButton: false, action: null }),
    [updateSnackbarData],
  );

  return <AppSnackbarStandalone snackbarData={snackbarData} onClose={handleClose} />;
};
